import { Modal } from "../shared/modal";
import { Button } from "../shared/button";
import type { CatalogItem } from "../../api/types";
import "./preview-modal.css";

function fmt(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function applyInflate(
  baseCents: number,
  method: "pct" | "flat",
  pct: number,
  flatCents: number,
  rounding: "none" | "nearest" | "up",
): number {
  let price =
    method === "flat"
      ? baseCents + flatCents
      : Math.round(baseCents / (1 - pct / 100));
  if (rounding === "nearest") price = Math.round(price / 100) * 100;
  else if (rounding === "up") price = Math.ceil(price / 100) * 100;
  return Math.max(price, baseCents);
}

interface PreviewModalProps {
  open: boolean;
  items: CatalogItem[];
  method: "pct" | "flat";
  pct: number;
  flatCents: number;
  rounding: "none" | "nearest" | "up";
  onConfirm: () => void;
  onCancel: () => void;
}

export function PreviewModal({
  open,
  items,
  method,
  pct,
  flatCents,
  rounding,
  onConfirm,
  onCancel,
}: PreviewModalProps) {
  const uninflatedItems = items.filter((i) =>
    i.variations.some((v) => !v.is_inflated && v.price_cents > 0),
  );

  const summaryParts = [
    method === "pct" ? `${pct}% fee` : `+$${(flatCents / 100).toFixed(2)} flat`,
    rounding === "none" ? "no rounding" : rounding === "nearest" ? "nearest $" : "round up",
  ];

  return (
    <Modal open={open} onClose={onCancel}>
      <div className="preview-modal-content">
        <div style={{ fontSize: "17px", fontWeight: 900, marginBottom: "4px" }}>
          Preview Price Changes
        </div>
        <div style={{ fontSize: "12px", color: "var(--ink3)", marginBottom: "14px" }}>
          {summaryParts.join(" \u00B7 ")}
        </div>

        <div style={{ maxHeight: "50vh", overflowY: "auto", marginBottom: "16px" }}>
          {uninflatedItems.map((item) =>
            item.variations
              .filter((v) => !v.is_inflated && v.price_cents > 0)
              .map((v) => {
                const newPrice = applyInflate(v.price_cents, method, pct, flatCents, rounding);
                return (
                  <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)", fontSize: "13px" }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>{item.name}{v.name && v.name !== "Regular" ? ` \u00B7 ${v.name}` : ""}</div>
                      <div style={{ fontSize: "11px", color: "var(--green)" }}>+{fmt(newPrice - v.price_cents)}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ textDecoration: "line-through", color: "var(--ink3)", fontFamily: "var(--mono)" }}>{fmt(v.price_cents)}</div>
                      <div style={{ fontFamily: "var(--mono)", fontWeight: 700, color: "var(--blue)" }}>{fmt(newPrice)}</div>
                    </div>
                  </div>
                );
              }),
          )}
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          <Button variant="outline" style={{ flex: 1 }} onClick={onCancel}>Cancel</Button>
          <Button variant="blue" style={{ flex: 1 }} onClick={onConfirm}>Confirm Inflate</Button>
        </div>
      </div>
    </Modal>
  );
}
